// Color tokens mirror the CSS custom properties defined in src/App.scss (:root).
// The swatch background is rendered live via var(--token) so it never drifts from
// App.scss; `value` is the literal hex/rgba shown as text for reference only.

export interface ColorToken {
  name: string; // token name without the leading `--`
  value: string; // literal hex / rgba, as declared in App.scss
}

export interface ColorGroup {
  title: string;
  tokens: ColorToken[];
}

export const COLOR_GROUPS: ColorGroup[] = [
  {
    title: 'Primary',
    tokens: [
      { name: 'primary', value: '#7136ed' },
      { name: 'primary-alpha-100', value: 'rgba(166, 108, 255, 0.1)' },
      { name: 'primary-alpha-400', value: '#a66cff' }
    ]
  },
  {
    title: 'Purple',
    tokens: [
      { name: 'purple', value: '#6c5ce7' },
      { name: 'purple-1', value: '#7a57d1' },
      { name: 'purple-2', value: '#8d6cd5' },
      { name: 'purple-3', value: '#9676e6' },
      { name: 'purple-4', value: '#9b5de5' },
      { name: 'purple-5', value: '#a28cff' },
      { name: 'purple-6', value: '#b6a5eb' },
      { name: 'purple-7', value: '#bfb1fe' },
      { name: 'purple-8', value: '#c7bdf5' },
      { name: 'purple-alpha-200', value: 'rgba(122, 87, 209, 0.2)' },
      { name: 'purple-gradient-1', value: 'linear-gradient(62deg, #c7bdf5, #bfb1fe)' }
    ]
  },
  {
    title: 'Blue',
    tokens: [
      { name: 'blue', value: '#007aff' },
      { name: 'blue-1', value: '#66799a' },
      { name: 'blue-2', value: '#2f80ed' },
      { name: 'blue-3', value: '#4c5cff' },
      { name: 'blue-4', value: '#6eb5ff' },
      { name: 'blue-5', value: '#6eb5ff' },
      { name: 'blue-6', value: '#4daff7' },
      { name: 'blue-7', value: '#91b9e7' },
      { name: 'blue-8', value: '#9dc4ec' },
      { name: 'blue-9', value: '#8bc7fe' },
      { name: 'blue-10', value: '#b8c4dd' },
      { name: 'blue-11', value: '#d5e4ff' },
      { name: 'blue-12', value: '#e6eff5' },
      { name: 'blue-alpha-70', value: 'rgba(92, 115, 160, 0.07)' },
      { name: 'blue-alpha-100', value: 'rgba(0, 122, 255, 0.1)' },
      { name: 'blue-alpha-200', value: 'rgba(47, 128, 237, 0.2)' },
      { name: 'blue-alpha-250', value: 'rgba(70, 96, 135, 0.25)' },
      { name: 'blue-alpha-350', value: 'rgba(70, 96, 135, 0.35)' },
      { name: 'blue-alpha-1000', value: 'rgba(102, 121, 154, 1)' },
      { name: 'blue-gradient-1', value: 'linear-gradient(62deg, #9dc4ec, #91b9e7)' },
      { name: 'blue-gradient-2', value: 'linear-gradient(180deg, rgba(255,255,255,0), #d5e4ff)' }
    ]
  },
  {
    title: 'Teal',
    tokens: [
      { name: 'teal', value: '#00bfa6' },
      { name: 'teal-1', value: '#018b7a' },
      { name: 'teal-2', value: '#02a38e' },
      { name: 'teal-3', value: '#dcfaf8' },
      { name: 'teal-alpha-200', value: 'rgba(0, 182, 155, 0.2)' },
      { name: 'teal-gradient-1', value: 'linear-gradient(62deg, #02a38e, #018b7a)' }
    ]
  },
  {
    title: 'Green',
    tokens: [
      { name: 'green', value: '#34c759' },
      { name: 'green-1', value: '#16db99' },
      { name: 'green-2', value: '#4ce417' },
      { name: 'green-3', value: '#719d72' },
      { name: 'green-4', value: '#a3d9a5' },
      { name: 'green-alpha-200', value: 'rgba(52, 199, 89, 0.2)' }
    ]
  },
  {
    title: 'Yellow',
    tokens: [
      { name: 'yellow', value: '#ecb900' },
      { name: 'yellow-1', value: '#ffd746' },
      { name: 'yellow-2', value: '#ffd580' }
    ]
  },
  {
    title: 'Orange',
    tokens: [
      { name: 'orange', value: '#ff5f00' },
      { name: 'orange-1', value: '#fcab4b' },
      { name: 'orange-2', value: '#f7c487' },
      { name: 'orange-gradient-1', value: 'linear-gradient(62deg, #f7c487, #fcab4b)' }
    ]
  },
  {
    title: 'Red',
    tokens: [
      { name: 'red', value: '#ff0100' },
      { name: 'red-1', value: '#b91c1c' },
      { name: 'red-2', value: '#eb5757' },
      { name: 'red-3', value: '#ff4b4a' },
      { name: 'red-4', value: '#ff6b6b' },
      { name: 'red-5', value: '#ff8281' },
      { name: 'red-6', value: '#ffabaa' },
      { name: 'red-alpha-200', value: 'rgba(235, 87, 87, 0.2)' },
      { name: 'red-gradient-1', value: 'linear-gradient(62deg, #ffabaa, #ff8281)' }
    ]
  },
  {
    title: 'Pink',
    tokens: [
      { name: 'pink', value: '#ff82ac' },
      { name: 'pink-1', value: '#fb9daf' },
      { name: 'pink-alpha-500', value: 'rgba(255, 224, 235, 0.5)' }
    ]
  },
  {
    title: 'Gray',
    tokens: [
      { name: 'gray', value: '#9e9e9e' },
      { name: 'gray-1', value: '#344054' },
      { name: 'gray-2', value: '#727475' },
      { name: 'gray-3', value: '#9aa5b1' },
      { name: 'gray-4', value: '#b0b0b0' },
      { name: 'gray-5', value: '#bdbdbd' },
      { name: 'gray-6', value: '#d5d7e5' },
      { name: 'gray-7', value: '#dfeaf2' },
      { name: 'gray-8', value: '#e0e0e0' },
      { name: 'gray-9', value: '#edebf0' },
      { name: 'gray-10', value: '#f5f5f5' },
      { name: 'gray-11', value: '#f8f9fd' },
      { name: 'gray-alpha-40', value: 'rgba(69, 69, 69, 0.04)' },
      { name: 'gray-alpha-200', value: 'rgba(154, 165, 177, 0.2)' },
      { name: 'gray-alpha-300', value: 'rgba(117, 117, 117, 0.3)' },
      { name: 'gray-alpha-500', value: 'rgba(213, 215, 229, 0.5)' },
      { name: 'gray-alpha-600', value: 'rgba(223, 234, 242, 0.6)' },
      { name: 'gray-alpha-1000', value: 'rgba(239, 240, 247, 1)' }
    ]
  },
  {
    title: 'Dark',
    tokens: [
      { name: 'dark', value: '#191b1b' },
      { name: 'dark-1', value: '#232323' },
      { name: 'dark-2', value: '#2e2e2e' },
      { name: 'dark-3', value: '#333333' },
      { name: 'dark-alpha-600', value: 'rgba(25, 27, 27, 0.6)' },
      { name: 'dark-alpha-800', value: 'rgba(25, 27, 27, 0.8)' }
    ]
  },
  {
    title: 'Black',
    tokens: [
      { name: 'black', value: '#000000' },
      { name: 'black-1', value: '#101828' },
      { name: 'black-2', value: '#181818' },
      { name: 'black-alpha-50', value: 'rgba(0, 0, 0, 0.05)' },
      { name: 'black-alpha-100', value: 'rgba(0, 0, 0, 0.1)' },
      { name: 'black-alpha-150', value: 'rgba(0, 0, 0, 0.15)' },
      { name: 'black-alpha-200', value: 'rgba(0, 0, 0, 0.2)' },
      { name: 'black-alpha-300', value: 'rgba(0, 0, 0, 0.3)' },
      { name: 'black-alpha-400', value: 'rgba(0, 0, 0, 0.4)' },
      { name: 'black-alpha-600', value: 'rgba(0, 0, 0, 0.6)' }
    ]
  },
  {
    title: 'White',
    tokens: [
      { name: 'white', value: '#ffffff' },
      { name: 'white-alpha-20', value: 'rgba(255, 255, 255, 0.02)' },
      { name: 'white-alpha-30', value: 'rgba(255, 255, 255, 0.03)' },
      { name: 'white-alpha-80', value: 'rgba(238, 237, 237, 0.08)' },
      { name: 'white-alpha-200', value: 'rgba(255, 255, 255, 0.2)' },
      { name: 'white-alpha-400', value: 'rgba(255, 255, 255, 0.4)' },
      { name: 'white-alpha-600', value: 'rgba(255, 255, 255, 0.6)' }
    ]
  },
  {
    title: 'Overlay & Gradients',
    tokens: [
      { name: 'overlay', value: 'rgba(128, 128, 128, 0.8)' },
      { name: 'gray-gradient-1', value: 'linear-gradient(0deg, rgba(128,128,128,0.8), rgba(128,128,128,0.8))' },
      { name: 'white-gradient-1', value: 'linear-gradient(rgba(255,255,255,0.4), rgba(238,237,237,0.08))' },
      { name: 'primary-gradient-1', value: 'radial-gradient(circle at top left, #f9fafc, #e3e8f0, var(--primary))' }
    ]
  }
];

// --primary-50 .. --primary-900 are NOT declared in App.scss — they are injected
// at runtime by DynamicThemeProvider (alpha steps of the org's brand primary color).
export const PRIMARY_SHADE_STEPS = [
  '50',
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900'
];
